import React, { useState } from 'react';
import { Button, Box, Typography, Alert, CircularProgress } from '@mui/material';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db, collections, ROLES } from '../../firebase.marketplace';

const AdminSetupButton = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const makeAdmin = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        throw new Error('You must be signed in to perform this action');
      }

      // Check if the user document exists
      const userRef = doc(db, collections.USERS, user.uid);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        // Update existing user document
        const userData = userDoc.data();
        
        // Update roles
        await updateDoc(userRef, {
          roles: {
            ...(userData.roles || {}),
            [ROLES.USER]: true,
            [ROLES.ADMIN]: true,
            [ROLES.ARTIST]: true,
            superadmin: true
          },
          updatedAt: new Date()
        });
      } else {
        // Create new user document
        await setDoc(userRef, {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || user.email.split('@')[0],
          photoURL: user.photoURL,
          createdAt: new Date(),
          roles: {
            [ROLES.USER]: true,
            [ROLES.ADMIN]: true,
            [ROLES.ARTIST]: true,
            superadmin: true
          }
        });
      }

      // Verify the update
      const updatedDoc = await getDoc(userRef);
      
      if (updatedDoc.exists()) {
        setResult('Admin privileges granted successfully!');
        console.log('Updated user data:', updatedDoc.data());
      } else {
        throw new Error('Failed to verify user update');
      }
    } catch (err) {
      console.error('Error making admin:', err);
      setError(err.message || 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 500, mx: 'auto', textAlign: 'center' }}>
      <Typography variant="h5" gutterBottom>
        Make Current User Admin
      </Typography>
      
      <Typography variant="body1" sx={{ mb: 3 }}>
        This will grant admin privileges to your current account.
      </Typography>
      
      <Button
        variant="contained"
        color="primary"
        onClick={makeAdmin}
        disabled={loading}
        sx={{ mb: 2 }}
      >
        {loading ? <CircularProgress size={24} /> : 'Make Me Admin'}
      </Button>
      
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      
      {result && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {result}
        </Alert>
      )}
    </Box>
  );
};

export default AdminSetupButton;
